import React, { useState, useEffect } from "react";
import axios from "axios";

// Function to start the worker
const createWorker = () => new Worker(new URL('./worker.js', import.meta.url));

const App = () => {
    const [scannedIds, setScannedIds] = useState(new Set());
    const [inputValue, setInputValue] = useState("");
    const [discrepancies, setDiscrepancies] = useState({ missing: [], extra: [] });
    const [showModal, setShowModal] = useState(false);
    const [worker, setWorker] = useState(null);

    // Tab state for switching between Missing and Extra
    const [activeTab, setActiveTab] = useState("missing");  // Default to missing tab

    const API_URL = 'https://jwapi.infosware-test.in';

    // Initialize Web Worker on component mount
    useEffect(() => {
        const newWorker = createWorker();
        newWorker.onmessage = (event) => {
            setScannedIds(prevIds => new Set([...prevIds, ...event.data]));
        };
        setWorker(newWorker);

        return () => newWorker.terminate();
    }, []);

    // Apply overflow hidden to body to remove scrolling
    useEffect(() => {
        document.body.style.overflow = "hidden";
        document.body.style.margin = "0";
        document.body.style.padding = "0";
        
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    // Handle input value and send to worker
    useEffect(() => {
        if (inputValue.trim() !== "") {
            let value = inputValue;
            let tags = [];
            while (value.length >= 24) {
                const tag = value.substring(0, 24);
                tags.push(tag);
                value = value.substring(24);
            }

            setScannedIds(prevIds => {
                const newIds = new Set(prevIds);
                tags.forEach(tag => newIds.add(tag));
                return newIds;
            });

            setInputValue(value);
        }
    }, [inputValue]);

    // Save scanned IDs to the database and compare
    const saveToDatabase = () => {
        axios.post(`${API_URL}/api/save`, { scannedIds: Array.from(scannedIds) })
            .then(() => axios.get(`${API_URL}/api/compare`))
            .then((response) => {
                const { missing, extra } = response.data;
                setDiscrepancies({ missing, extra });
                setShowModal(true);
            });
    };

    // Restart scanning process
    const restart = () => {
        setScannedIds(new Set());
        setDiscrepancies({ missing: [], extra: [] });
        setShowModal(false);
        setInputValue("");
    };

    // Close modal
    const closeModal = () => {
        setScannedIds(new Set());
        setShowModal(false);
    };

    // Switch between tabs
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    return (
        <div style={mainContainerStyle}>
            <h1 style={headingStyle}>Scan IDs</h1>

            {/* Input Field */}
            <div style={inputWrapperStyle}>
                <input
                    type="text"
                    placeholder="Scan or Enter ID"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    style={inputStyle}
                />
            </div>

            {/* Scanned IDs Table */}
            <div style={tableWrapperStyle}>
    <div style={scrollableContainerStyle}>
        {!showModal && (
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Sr.No</th>
                        <th style={tableHeaderStyle}>Scanned ID</th>
                        <th style={tableHeaderStyle}>Count ({scannedIds.size})</th> {/* Showing the total count here */}
                    </tr>
                </thead>
                <tbody>
                    {Array.from(scannedIds).map((id, index) => (
                        <tr key={index}>
                            <td style={tableCellStyle}>{index + 1}</td>  {/* Sr.No showing index + 1 */}
                            <td style={tableCellStyle}>
                                {id.rfid ? `${id.rfid} - ${id.itemType}` : id}
                            </td>
                            <td style={tableCellStyle}></td>  {/* Empty cell for Count in each row */}
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
</div>


            {/* Action Buttons */}
            <div style={actionButtonsWrapperStyle}>
                <button onClick={saveToDatabase} style={compareButtonStyle}>
                    Compare
                </button>
                <button onClick={restart} style={restartButtonStyle}>
                    Restart
                </button>
            </div>

            {/* Modal */}
            {showModal && (
    <div style={modalOverlayStyle}>
        <div style={modalContentStyle}>
            {/* Modal Header */}
            <div style={modalHeaderStyle}>
                <h3 style={modalTitleStyle}>Discrepancies Found</h3>
                <button onClick={closeModal} style={closeModalButtonStyle}>X</button>
            </div>

            {/* Tab Buttons */}
            <div style={tabButtonsStyle}>
                <button
                    onClick={() => handleTabChange("missing")}
                    style={{
                        ...tabButtonStyle,
                        backgroundColor: activeTab === "missing" ? "#4b80b7" : "#ddd",
                    }}
                >
                    Missing ({discrepancies.missing.length}) {/* Displaying the missing count */}
                </button>
                <button
                    onClick={() => handleTabChange("extra")}
                    style={{
                        ...tabButtonStyle,
                        backgroundColor: activeTab === "extra" ? "#4b80b7" : "#ddd",
                    }}
                >
                    Extra ({discrepancies.extra.length}) {/* Displaying the extra count */}
                </button>
            </div>

            {/* Tab Content */}
            <div style={tabContentStyle}>
                {activeTab === "missing" && discrepancies.missing.length > 0 && (
                    <div style={scrollableContainerStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={tableHeaderStyle}>RFID</th>
                                    <th style={tableHeaderStyle}>Item Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {discrepancies.missing.map((item, index) => (
                                    <tr key={index}>
                                        <td style={tableCellStyle}>{item.rfid}</td>
                                        <td style={tableCellStyle}>{item.itemType}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === "extra" && discrepancies.extra.length > 0 && (
                    <div style={scrollableContainerStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={tableHeaderStyle}>RFID</th>
                                    <th style={tableHeaderStyle}>Item Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {discrepancies.extra.map((item, index) => (
                                    <tr key={index}>
                                        <td style={tableCellStyle}>{item.rfid}</td>
                                        <td style={tableCellStyle}>{item.itemType || "Not Available"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    </div>
)}

        </div>
    );
};

// Styles for tabs and tab content
const tabButtonsStyle = {
    display: "flex",
    justifyContent: "center",
    marginBottom: "1px",
    marginTop: "8px",
};

const tabButtonStyle = {
    padding: "10px 13px",
    fontSize: "12px",
    cursor: "pointer",
    border: "none",
    borderRadius: "8px 8px 0 0",
    margin: "0 5px",
    transition: "background-color 0.3s ease",
    borderRadius: "3px",
};

const tabContentStyle = {
    padding: "10px",
};


const scrollableContainerStyle = {
    maxHeight: "380px", // Adjust based on the row height
    overflowY: "auto",  // Make the list scrollable
};

// Keep the page's layout fixed and avoid scrolling the entire page
const mainContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0px",
    backgroundColor: "#f4f7fc",
    minHeight: "100vh",
    fontFamily: "'Roboto', sans-serif",
    overflow: "hidden", // Prevent page from scrolling
    position: "relative", // Add this to allow absolute positioning for buttons
    margin: "0", // Ensure there is no margin around the container
    boxSizing: "border-box", // Ensure padding is included in the element's width
};

const headingStyle = {
    fontSize: "22px",             // Same font size as table header
    fontWeight: "600",            // Bold font weight
    color: "white",               // White text for visibility on gradient
    marginTop: "0",   
    marginBottom:"15px",               // Removed any margins to eliminate space above
    background: "linear-gradient(to right, #312d2c, #1d5892)", // Gradient background
    padding: "12px 15px",         // Padding same as table header
    borderRadius: "2px",          // Rounded corners (optional)
    textAlign: "center",          // Center the text
    width: "100%",                // Set width to 100% to match the table
    boxSizing: "border-box",      // Ensure padding is included in the element's width
};

const inputWrapperStyle = {
    width: "100%",
    maxWidth: "350px",
    marginBottom: "0px",
    
};

const inputStyle = {
    width: "100%",
    padding: "10px",
    fontSize: "14px",
    borderRadius: "8px",
    border: "1px solid #ccd6e0",
    marginBottom: "13px",
    outline: "none",
    backgroundColor: "#ffffff",
};

const tableWrapperStyle = {
    width: "100%",
    maxWidth: "800px",
    marginBottom: "20px",
};


const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
};

const tableHeaderStyle = {
    backgroundColor: "#6fa1d6",  
    color: "white",
    textAlign: "left",
    padding: "4px 7px",
    fontSize: "14px",
    position: "sticky",  // Make the header sticky
    top: 0,              // Stick it to the top of the table
    zIndex: 1,
};


const tableCellStyle = {
    padding: "8px 11px",
    textAlign: "left",
    borderBottom: "1px solid #f0f0f0",
    fontSize: "12px",
};

const actionButtonsWrapperStyle = {
    display: "flex",
    gap: "3px", // Maintain the gap between the buttons
    justifyContent: "center",
    flexDirection: "row",
    position: "fixed", // Keep buttons fixed at the bottom
    bottom: "5px",    // Position them 20px from the bottom
    width: "100%",
    maxWidth: "700px",
    padding: "0 5px",
    marginBottom: "0px", // Add space below the button container itself
};


const compareButtonStyle = {
    padding: "10px 13px",
    backgroundColor: "#1d5892",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    width: "45%",
};

const restartButtonStyle = {
    padding: "10px 13px",
    backgroundColor: "#f44336",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    width: "45%",
};
const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.7)", // Darker background for better focus
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "background-color 0.3s ease-in-out", // Smooth transition
};


const modalContentStyle = {
    backgroundColor: "#ffffff",
    padding: "0px",
    borderRadius: "12px",  // Increased radius for a more rounded look
    maxWidth: "950px",     // Larger width for better content display
    width: "85%",
    textAlign: "center",
    position: "relative",
    maxHeight: "80vh",     // Maximum height to ensure it doesn’t overflow
    overflowY: "auto",     // Make it scrollable if content overflows
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)", // Soft shadow for the modal
    animation: "fadeIn 0.5s ease-in-out", // Animation for smooth entrance
};


const fadeInAnimation = `
    @keyframes fadeIn {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
`;

document.head.insertAdjacentHTML(
    'beforeend',
    `<style>${fadeInAnimation}</style>`
);

const modalHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",  // Space between the title and the close button
    alignItems: "center",            // Vertically center them
    background: "linear-gradient(to right, #312d2c, #1d5892)",  // Gradient background
    padding: "12px 15px",            // Padding to match the original design
    borderRadius: "8px 8px 0 0",     // Rounded top corners
};

const modalTitleStyle = {
    fontSize: "18px",
    fontWeight: "600",
    color: "white",                 // White text color for the title
    margin: "0",                    // Remove any default margins
    textAlign: "left",              // Left-align the title text
};

const closeModalButtonStyle = {
    background: "none",              // Transparent background
    border: "none",                  // Remove border
    fontSize: "24px",                // Larger button size
    color: "#e74c3c",                // Red color for the close button
    cursor: "pointer",              // Pointer cursor on hover
    fontWeight: "bold",              // Make the close button bold
    padding: "0",                    // Remove default padding for the button
};


export default App;
